import { createSlice, createAsyncThunk, createAction, PayloadAction } from '@reduxjs/toolkit'
import { branchesAPI, BranchOption } from 'api/branches'
import { getUserPreference, removeUserPreference, setUserPreference } from 'services/userPreferences'

export interface BranchState {
 selectedBranchId: string | null
 selectedBranch: BranchOption | null
 branches: BranchOption[]
 viewMode: 'global' | 'branch'
 loading: boolean
 canSeeGlobalView: boolean
}

function clearUnscopedBranchContext(): void {
 try {
  // Legacy values have no tenant/user owner and may collide across accounts.
  localStorage.removeItem('selected_branch_id')
  localStorage.removeItem('branch_view_mode')
 } catch { /* browser storage is optional */ }
}

function readAuthUser(): { current_branch_id?: string | null; can_use_global_view?: boolean } | null {
 try {
  const raw = localStorage.getItem('authUser')
  if (!raw) return null
  const parsed = JSON.parse(raw) as { user?: { current_branch_id?: string | null; can_use_global_view?: boolean } } & { current_branch_id?: string | null; can_use_global_view?: boolean }
  return parsed.user ?? parsed
 } catch {
  return null
 }
}

clearUnscopedBranchContext()

export function resolveInitialBranchContext(
 savedView: 'global' | 'branch' | null,
 savedBranchId: string | null,
 authUser: { can_use_global_view?: boolean } | null,
): { selectedBranchId: string | null; viewMode: 'global' | 'branch' } {
 if (savedView === 'global' && authUser?.can_use_global_view) {
  return { selectedBranchId: null, viewMode: 'global' }
 }
 const canUseGlobalView = Boolean(authUser?.can_use_global_view)
 const shouldStartInBranchView = !canUseGlobalView || savedView === 'branch' || Boolean(savedBranchId)
 return {
  // The saved entity ID is held until fetchBranches validates current access.
  selectedBranchId: null,
  viewMode: shouldStartInBranchView ? 'branch' : 'global',
 }
}

function readInitialBranchContext(): { selectedBranchId: string | null; viewMode: 'global' | 'branch' } {
 return resolveInitialBranchContext(
  getUserPreference('branch.viewMode'),
  getUserPreference('branch.preferredId'),
  readAuthUser(),
 )
}

const initialBranchContext = readInitialBranchContext()

const initialState: BranchState = {
 selectedBranchId: initialBranchContext.selectedBranchId,
 selectedBranch: null,
 branches: [],
 viewMode: initialBranchContext.viewMode,
 loading: false,
 canSeeGlobalView: false,
}

export const branchChanged = createAction('branch/changed')

export const fetchBranches = createAsyncThunk(
 'branch/fetchBranches',
 async (_, { rejectWithValue }) => {
 try {
 const res = await branchesAPI.listAccessible()
 return res
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || 'Failed to fetch branches')
 }
 }
)

const branchSlice = createSlice({
 name: 'branch',
 initialState,
 reducers: {
 selectBranch: (state, action: PayloadAction<string | null>) => {
 if (action.payload && state.branches.length > 0 && !state.branches.some((branch) => branch.id === action.payload)) return
 if (!action.payload && !state.canSeeGlobalView) return
 state.selectedBranchId = action.payload
 state.viewMode = action.payload ? 'branch' : 'global'
 localStorage.setItem('branch_view_mode', state.viewMode)
 if (action.payload) {
 localStorage.setItem('selected_branch_id', action.payload)
     setUserPreference('branch.preferredId', String(action.payload))
     setUserPreference('branch.viewMode', 'branch')
 state.selectedBranch = state.branches.find(b => b.id === action.payload) ?? null
 } else {
 localStorage.removeItem('selected_branch_id')
     removeUserPreference('branch.preferredId')
     setUserPreference('branch.viewMode', 'global')
 state.selectedBranch = null
 }
 window.dispatchEvent(new CustomEvent('branch:changed'))
 },
 setCanSeeGlobalView: (state, action: PayloadAction<boolean>) => {
 state.canSeeGlobalView = action.payload
 },
 clearBranch: (state) => {
 state.selectedBranchId = null
 state.selectedBranch = null
 state.branches = []
 state.viewMode = 'global'
 state.canSeeGlobalView = false
 localStorage.removeItem('selected_branch_id')
 localStorage.setItem('branch_view_mode', 'global')
 },
 },
 extraReducers: (builder) => {
 builder
 .addCase(fetchBranches.pending, (state) => {
 state.loading = true
 })
 .addCase(fetchBranches.fulfilled, (state, action: any) => {
 state.loading = false
 const branches = action.payload?.data?.branches ?? []
 state.branches = branches
   const savedView = getUserPreference('branch.viewMode')
   const savedBranchId = getUserPreference('branch.preferredId')
   const authUser = readAuthUser()
   const canUseGlobalView = state.canSeeGlobalView || Boolean(authUser?.can_use_global_view)
   const requestedId = state.selectedBranchId ?? savedBranchId

   if (savedView === 'global' && canUseGlobalView) {
    state.selectedBranchId = null
    state.selectedBranch = null
    state.viewMode = 'global'
    localStorage.removeItem('selected_branch_id')
    localStorage.setItem('branch_view_mode', 'global')
    return
   }

   const validRequested = requestedId
    ? branches.find((branch: BranchOption) => String(branch.id) === String(requestedId)) ?? null
    : null
   if (validRequested) {
    state.selectedBranchId = validRequested.id
    state.selectedBranch = validRequested
    state.viewMode = 'branch'
    localStorage.setItem('selected_branch_id', validRequested.id)
    localStorage.setItem('branch_view_mode', 'branch')
    return
   }

   if (savedBranchId) removeUserPreference('branch.preferredId')
   const canonicalBranch = authUser?.current_branch_id
    ? branches.find((branch: BranchOption) => String(branch.id) === String(authUser.current_branch_id)) ?? null
    : null
   const safeFallback = canonicalBranch ?? (!canUseGlobalView
    ? branches.find((branch: BranchOption) => branch.is_main) ?? branches[0] ?? null
    : null)
   if (safeFallback) {
    state.selectedBranchId = safeFallback.id
    state.selectedBranch = safeFallback
    state.viewMode = 'branch'
    localStorage.setItem('selected_branch_id', safeFallback.id)
    localStorage.setItem('branch_view_mode', 'branch')
   } else {
    state.selectedBranchId = null
    state.selectedBranch = null
    state.viewMode = 'global'
    localStorage.removeItem('selected_branch_id')
    if (canUseGlobalView) localStorage.setItem('branch_view_mode', 'global')
    else localStorage.removeItem('branch_view_mode')
   }
 })
 .addCase(fetchBranches.rejected, (state) => {
 state.loading = false
 })
 },
})

export const { selectBranch, setCanSeeGlobalView, clearBranch } = branchSlice.actions
export default branchSlice.reducer
